import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Form, InputField, Alert, Text } from '@adc/ui-morpheus'
import { encryptLoanNumber } from '../../providers/ingestion'

/**
 * LoanNumberEntry — Loan Number Encryption Component (IGNITE-1804)
 *
 * This component is opened in a new tab/window from Salesforce.
 * It encrypts a loan number via Vault and copies the resulting UUID
 * to the user's clipboard for pasting back into a Salesforce field.
 *
 * ==================== INTEGRATION CONTRACT ====================
 *
 * URL Pattern:
 *   {morpheusHost}/embed/loan/:orgId?servicer=<ServicerName>
 *
 * URL Parameters:
 *   - orgId (path): The servicer's organization ID for encryption.
 *     Salesforce pulls this from the servicer account record (org ID field).
 *     If blank or "0", encryption is rejected (security requirement).
 *
 *   - servicer (query): Human-readable servicer name (e.g. "Cenlar FSB").
 *     Displayed in the UI so the agent can confirm context.
 *     Salesforce populates this from the account they selected.
 *
 * ==================== AUTHENTICATION ==========================
 *
 * - User MUST have an active Morpheus session (auth cookie on .aucn.io domain).
 * - If not authenticated, they are redirected to /trinity/login.
 * - User MUST have the LNM (LOAN_NUMBER_MANAGER) authority.
 * - This is enforced at the server level in src/server/index.js.
 *
 * ==================== FLOW ====================================
 *
 * Step 1 (Input):
 *   - Displays servicer name (from URL) + loan number input
 *   - User enters clear-text loan number
 *   - On "Encrypt" click: calls encryptLoanNumber GraphQL mutation
 *   - The graph resolver calls PUT /v1/mlhdata/encrypt on resi-cs-thft internally
 *   - Sends loan_no + servicer_loan_no (same value, per agreement)
 *   - Does NOT send seller_alternate_loan_no (per team decision)
 *
 * Step 2 (Success):
 *   - Auto-copies returned UUID to user's clipboard
 *   - Displays UUID on screen
 *   - Provides "Copy Again" button in case clipboard was overwritten
 *   - User closes window and pastes UUID into SF "Encrypted Loan Number" field
 *
 * Error Handling:
 *   - If encryption fails, loan number stays in the input (not cleared)
 *   - Error message displayed, user can retry
 *   - If org ID is invalid (blank/zero), encryption is blocked client-side
 *
 * Security:
 *   - beforeunload warning if user tries to close with loan number entered
 *   - No clear-text loan number in console logs
 *   - UUID is the only value that leaves this component (via clipboard)
 *
 * ==================== TIMING SCENARIOS ========================
 *
 * This component supports three scenarios (all use the same UI):
 *
 * 1. UPFRONT (pre-lead-creation):
 *    - No lead or listing exists yet
 *    - Concierge is on the phone, enters loan number immediately
 *    - UUID is pasted into the lead form before saving
 *    - This is the most common scenario (~majority of cases)
 *
 * 2. POST-LEAD-CREATION (lead exists, not yet synced to Morpheus):
 *    - Property lead already created in SF
 *    - Servicer name auto-populates in URL from the lead's account
 *    - UUID saved to property lead, later syncs to Morpheus
 *
 * 3. POST-LISTING-CREATION (listing exists in Morpheus):
 *    - Listing already synced to Morpheus
 *    - Same iframe flow, UUID will be associated to the listing
 *    - SF triggers "Send to Morpheus" to sync the UUID
 *
 * ==================== SALESFORCE SIDE =========================
 *
 * SF is responsible for:
 *   - Generating the parameterized hyperlink (org ID from account)
 *   - Having an "Encrypted Loan Number" field on the lead/property
 *   - Syncing UUID to Morpheus property/listing via "Send to Morpheus"
 *   - Only showing the hyperlink when UUID is not yet populated
 *
 * ==================== ENCRYPTION API ==========================
 *
 * GraphQL Mutation: encryptLoanNumber (on resi-auction-graph-api)
 * Auth: Requires LNM authority (enforced by @requiresOneOfAuthorities directive)
 *
 * Mutation:
 *   mutation EncryptLoanNumber($input: EncryptLoanNumberInput!) {
 *     encryptLoanNumber(input: $input) { uuid, success }
 *   }
 *
 * Input:
 *   { org_id: "<orgId>", loan_number: "<cleartext>" }
 *
 * Under the hood, the graph resolver calls:
 *   PUT <RESI_CS_THFT>/resi/thft/v1/mlhdata/encrypt
 *   { data_encrypt_requests: [{ parent_seller_id, data_to_encrypt: { loan_no, servicer_loan_no } }] }
 *
 * Response:
 *   { uuid: "...", success: true }
 *
 * The returned UUID is what gets stored on the listing as loan_number_uuid.
 */

const STEP = {
  INPUT: 'input',
  SUCCESS: 'success',
}

/**
 * Copy text to the user's clipboard.
 * Uses navigator.clipboard API first (modern), falls back to execCommand('copy')
 * for iframe contexts where clipboard API is restricted without
 * allow="clipboard-write" on the iframe element.
 */
const copyToClipboard = async text => {
  // Try modern Clipboard API first
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Fallback: create a temporary textarea, select it, and use execCommand
    // This works inside iframes where the Clipboard API is blocked
    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      const success = document.execCommand('copy')
      document.body.removeChild(textarea)
      return success
    } catch {
      return false
    }
  }
}

const LoanNumberEntry = ({ match, user }) => {
  // orgId: servicer's organization ID for Vault encryption (from SF account)
  const { orgId } = match.params

  const [servicerName, setServicerName] = useState('')

  // ==================== AUTHORITY CHECK ====================
  // LNM (LOAN_NUMBER_MANAGER) authority required to encrypt loan numbers.
  // Each embedded widget is responsible for its own authority validation.
  if (!user?.authorities?.includes('LNM')) {
    return (
      <Form padded>
        <Form.Section title='Loan Number Encryption'>
          <Alert
            role='error'
            header='Unauthorized'
            content='You do not have the required permissions (LNM) to access this tool. Please contact your administrator.'
            hidden={false}
            showIcon
          />
        </Form.Section>
      </Form>
    )
  }

  // ==================== UPFRONT VALIDATION ====================
  // If org ID is missing, blank, or zero, the form must NOT render.
  // This is a security requirement — encrypting without a valid servicer
  // account would store data in the wrong Vault partition.
  const isValidOrgId = orgId && orgId !== '0' && orgId.trim() !== ''

  if (!isValidOrgId) {
    return (
      <Form padded>
        <Form.Section title='Loan Number Encryption'>
          <Alert
            role='error'
            header='Invalid Configuration'
            content='Cannot proceed — no valid servicer organization ID was provided in the URL. Please close this window and ensure a servicer account with an org ID is selected in Salesforce before generating the encryption link.'
            hidden={false}
            showIcon
          />
        </Form.Section>
      </Form>
    )
  }

  const [loanNumber, setLoanNumber] = useState('')
  const [uuid, setUuid] = useState('')
  const [step, setStep] = useState(STEP.INPUT)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [copyFeedback, setCopyFeedback] = useState('')
  const [copySuccess, setCopySuccess] = useState(true)

  /**
   * Encrypt the loan number via resi-cs-thft Vault endpoint.
   * On success: stores UUID in state, auto-copies to clipboard, transitions to Step 2.
   * On failure: keeps loan number in field, displays error, allows retry.
   */
  const handleEncrypt = async e => {
    e.preventDefault()

    // Client-side validation: loan number required
    if (!loanNumber.trim()) {
      setErrorMessage('Loan number is required.')
      return
    }

    setIsLoading(true)
    setErrorMessage('')

    try {
      const result = await encryptLoanNumber(
        parseInt(orgId, 10),
        loanNumber.trim()
      )

      // Store UUID and auto-copy to clipboard
      setUuid(result.uuid)
      await copyToClipboard(result.uuid)

      // Transition to success view (Step 2)
      setStep(STEP.SUCCESS)
    } catch (err) {
      // On failure: loan number stays in the field (NOT cleared)
      // This is a requirement — user should be able to retry without re-entering
      const message =
        err?.body?.message ||
        err?.message ||
        'Failed to encrypt loan number. Please try again.'
      setErrorMessage(message)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * "Copy Again" button handler.
   * Allows user to re-copy UUID if clipboard was overwritten.
   * Shows brief feedback to confirm copy succeeded.
   */
  const handleCopyAgain = async () => {
    const success = await copyToClipboard(uuid)
    setCopySuccess(success)
    setCopyFeedback(
      success
        ? 'Copied!'
        : 'Failed to copy. Please select the UUID text and copy manually.'
    )
    if (success) {
      setTimeout(() => setCopyFeedback(''), 2000)
    }
  }

  /**
   * Warn user if they try to close the window while:
   * - They have entered a loan number but haven't encrypted yet
   * This prevents accidental loss of the clear-text loan number
   * that would require the agent to ask the homeowner again.
   */
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (step === STEP.INPUT && loanNumber.trim()) {
        e.preventDefault()
        // NOTE: Modern browsers (Chrome 51+, Firefox 44+, Safari 9.1+) ignore custom
        // beforeunload messages for security reasons. The browser always shows its own
        // generic "Changes you made may not be saved." prompt. Setting returnValue to
        // any truthy string is only needed to trigger the prompt to appear.
        e.returnValue = 'unsaved'
      }
    }

    window?.addEventListener('beforeunload', handleBeforeUnload)
    return () => window?.removeEventListener('beforeunload', handleBeforeUnload)
  }, [step, loanNumber])

  useEffect(() => {
    // Servicer name passed as query param for display purposes
    const urlParams = new URLSearchParams(window?.location?.search)
    setServicerName(urlParams.get('servicer') || 'Unknown Servicer')
  }, [])

  // ==================== STEP 2: SUCCESS VIEW ====================
  // Shows the encrypted UUID and provides a "Copy Again" button.
  // The UUID was auto-copied to clipboard on transition to this step.
  if (step === STEP.SUCCESS) {
    return (
      <Form padded>
        <Form.Section title='Loan Number Encryption'>
          {/* Success notification — UUID auto-copied to clipboard */}
          <Alert
            role='success'
            header='Encrypted Loan Number has been copied to your clipboard!'
            content='Please paste into the Salesforce "Encrypted Loan Number" field.'
            hidden={false}
            showIcon
          />

          {/* Display UUID so user can also manually select/copy if needed */}
          <InputField
            elmId='loan-number'
            type='text'
            size='large'
            placeholder='UUID'
            value={uuid}
            label='UUID'
            readOnly
          />

          {/* Feedback after clicking "Copy Again" */}
          {copyFeedback && (
            <Alert
              role={copySuccess ? 'success' : 'error'}
              header={copyFeedback}
              hidden={false}
              showIcon
            />
          )}
          <Form.Button
            type='button'
            size='large'
            fluid
            primary
            onClick={handleCopyAgain}
            data-elm-id='copy-again'
          >
            Copy Again
          </Form.Button>
          {/* 
          // leaving this commented for now. We may want to include this feature later
          <Form.Button
            type='button'
            size='large'
            fluid
            onClick={() => {
              setStep(STEP.INPUT)
              setUuid('')
              setLoanNumber('')
              setCopyFeedback('')
            }}
            data-elm-id='encrypt-another'
          >
            Encrypt Another
          </Form.Button> */}
        </Form.Section>
      </Form>
    )
  }

  // ==================== STEP 1: INPUT VIEW ====================
  // Shows servicer name (from URL) and loan number input.
  // User enters the clear-text loan number and clicks "Encrypt".
  return (
    <Form onSubmit={handleEncrypt} padded>
      <Form.Section title='Loan Number Encryption'>
        {/* Servicer name from URL param — confirms context for the agent */}
        <Text style={{ marginBottom: 12 }}>Servicer</Text>
        <Text weight='heavy' style={{ marginBottom: 12 }}>
          {servicerName}
        </Text>
        {/* Error notification — persists until next action. Loan number is NOT cleared on error */}
        {errorMessage && (
          <Alert
            role='error'
            header='Encryption Error'
            content={errorMessage}
            hidden={false}
            showIcon
          />
        )}

        {/* Loan number input — uses ui-morpheus InputField component */}
        <InputField
          elmId='loan-number'
          type='text'
          size='large'
          placeholder='Enter loan number'
          value={loanNumber}
          onChange={e => setLoanNumber(e.target.value)}
          disabled={isLoading}
          autoComplete='off'
          label='Loan Number'
        />
        <Form.Button
          type='submit'
          size='large'
          fluid
          primary
          loading={isLoading}
          disabled={isLoading || !loanNumber.trim()}
          data-elm-id='encrypt-loan-number'
        >
          Encrypt
        </Form.Button>
      </Form.Section>
    </Form>
  )
}

const mapStateToProps = store => ({
  user: store.user,
})

export default connect(mapStateToProps)(LoanNumberEntry)
